<script lang="ts" setup>
import { useEventListener } from '@vueuse/core';
import { usePageGuideStore } from '~/store/pageGuide';
import { BUBBLE_WIDTH, bubblePlacement, needsNavigation, type GuideRect } from '~/utils/page-guide';
import { anchorKind, findAnchor, isVisibleOnScreen } from '~/utils/page-snapshot';
import type { GuideStep } from '~/utils/agent-chat';

// The spotlight of a page guide of Suge (pos-agent docs/chat-assistant-v1.md, section 8). It
// never clicks, types, or saves. The user does each click.

const FIND_TIMEOUT_MS = 5000;
const FIND_INTERVAL_MS = 200;
const RING_PADDING = 6;
// A click on a target can start a page change. The next step waits for it, so that the guide does
// not cancel the page change of the click.
const CLICK_NAVIGATION_WAIT_MS = 1500;

const { t } = useI18n();
const guide = usePageGuideStore();
const route = useRoute();
const router = useRouter();

type Status = 'finding' | 'shown' | 'hidden' | 'missing' | 'page';
const status = ref<Status>('page');
const rect = ref<GuideRect | null>(null);
const viewport = ref({ width: window.innerWidth, height: window.innerHeight });

let target: Element | null = null;
let findTimer: ReturnType<typeof setInterval> | undefined;
let frame: number | undefined;
let scrolled = false;
let advancedByClick = false;

const release = () => {
  if (findTimer !== undefined) clearInterval(findTimer);
  if (frame !== undefined) cancelAnimationFrame(frame);
  findTimer = undefined;
  frame = undefined;
  target = null;
  rect.value = null;
};

// Each frame reads the place of the element, so the ring follows a scroll, a resize, and a tab change.
const follow = () => {
  if (target === null) return;
  if (!target.isConnected) {
    // A re-render replaced the element. Find it again.
    const step = guide.step;
    if (step) locate(step);
    return;
  }
  const visible = isVisibleOnScreen(target);
  status.value = visible ? 'shown' : 'hidden';
  const box = visible ? target.getBoundingClientRect() : null;
  if (box && !scrolled) {
    scrolled = true;
    // A sticky action bar is already on the screen, and a scroll moves only the page under it.
    const onScreen = box.top >= 0 && box.bottom <= window.innerHeight;
    if (!onScreen) target.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
  rect.value = box && { top: box.top, left: box.left, width: box.width, height: box.height };
  viewport.value = { width: window.innerWidth, height: window.innerHeight };
  frame = requestAnimationFrame(follow);
};

// The page of a step can still load, so the search waits for the element for a short time.
const locate = (step: GuideStep) => {
  release();
  if (step.target === null) {
    status.value = 'page';
    return;
  }
  const anchor = step.target;
  status.value = 'finding';
  const started = Date.now();
  const attempt = () => {
    const found = findAnchor(document, anchor);
    if (found) {
      if (findTimer !== undefined) clearInterval(findTimer);
      findTimer = undefined;
      target = found;
      follow();
    } else if (Date.now() - started > FIND_TIMEOUT_MS) {
      if (findTimer !== undefined) clearInterval(findTimer);
      findTimer = undefined;
      status.value = 'missing';
    }
  };
  findTimer = setInterval(attempt, FIND_INTERVAL_MS);
  attempt();
};

const routeChange = (timeoutMs: number) =>
  new Promise<void>(resolve => {
    let stop = () => {};
    const timer = setTimeout(() => {
      stop();
      resolve();
    }, timeoutMs);
    stop = watch(
      () => route.fullPath,
      () => {
        clearTimeout(timer);
        stop();
        resolve();
      },
    );
  });

const elsewhere = (step: GuideStep) =>
  needsNavigation(
    step.path,
    { path: route.path, search: window.location.search },
    guide.steps[guide.index - 1]?.path ?? null,
  );

const showStep = async () => {
  release();
  scrolled = false;
  const fromClick = advancedByClick;
  advancedByClick = false;
  const step = guide.step;
  if (!step) return;
  if (elsewhere(step) && fromClick) {
    status.value = 'finding';
    await routeChange(CLICK_NAVIGATION_WAIT_MS);
    // The user can go to another step during the page change.
    if (guide.step !== step) return;
  }
  if (elsewhere(step)) {
    if (router.resolve(step.path).matched.length === 0) {
      status.value = 'missing';
      return;
    }
    status.value = 'finding';
    try {
      await navigateTo(step.path);
    } catch {
      // A route guard can refuse the page.
      if (guide.step === step) status.value = 'missing';
      return;
    }
    if (guide.step !== step) return;
  }
  locate(step);
};

// `active` is in the source, because a close on the first step does not change the index.
watch(() => [guide.run, guide.index, guide.active], showStep);
// The layout removes the spotlight when the chat is hidden. A guide that stays active would show
// again later on a page that the user did not expect.
onBeforeUnmount(() => {
  release();
  guide.close();
});

// A click on a button or a tab of the step goes to the next step. A field keeps its step, because
// the user types there, and the user presses "Next".
useEventListener(
  document,
  'click',
  (event: MouseEvent) => {
    const step = guide.step;
    if (!step?.target || target === null || status.value !== 'shown') return;
    if (anchorKind(step.target) === 'field') return;
    if (!(event.target instanceof Node) || !target.contains(event.target)) return;
    // The click must reach the page first, for example to open the tab of the next step.
    setTimeout(() => {
      // The user can close the guide or change the step before this runs.
      if (guide.step !== step) return;
      advancedByClick = true;
      guide.next();
    }, 0);
  },
  { capture: true },
);

// Escape first closes an open dialog, menu, or select list, and the guide stays. A tooltip and a
// snackbar are overlays too, but Escape does not close them. The capture phase
// reads the overlay before Vuetify closes it.
useEventListener(
  window,
  'keydown',
  (event: KeyboardEvent) => {
    if (!guide.active || event.key !== 'Escape') return;
    if (document.querySelector('.v-overlay--active:not(.v-tooltip, .v-snackbar)')) return;
    guide.close();
  },
  { capture: true },
);

const ringStyle = computed(() => {
  const box = rect.value;
  if (!box) return {};
  return {
    top: `${box.top - RING_PADDING}px`,
    left: `${box.left - RING_PADDING}px`,
    width: `${box.width + RING_PADDING * 2}px`,
    height: `${box.height + RING_PADDING * 2}px`,
  };
});

const bubbleStyle = computed(() => {
  const place = bubblePlacement(
    rect.value && {
      ...rect.value,
      top: rect.value.top - RING_PADDING,
      height: rect.value.height + RING_PADDING * 2,
    },
    viewport.value,
  );
  return {
    width: `${BUBBLE_WIDTH}px`,
    left: `${place.left}px`,
    ...(place.top !== undefined && { top: `${place.top}px` }),
    ...(place.bottom !== undefined && { bottom: `${place.bottom}px` }),
  };
});

const note = computed(() => {
  if (status.value === 'finding') return t('chat.guide.finding');
  if (status.value === 'hidden') return t('chat.guide.hidden');
  if (status.value === 'missing') return t('chat.guide.not_found');
  return '';
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="guide.active && guide.step"
      class="page-guide"
      data-suge-ignore
    >
      <div
        v-if="rect"
        class="page-guide__ring"
        :style="ringStyle"
      />
      <div
        class="page-guide__bubble"
        :style="bubbleStyle"
        role="dialog"
        :aria-label="t('chat.title')"
      >
        <div class="page-guide__head">
          <v-icon
            color="primary"
            size="18"
          >
            mdi-creation
          </v-icon>
          <span class="page-guide__count">
            {{ t('chat.guide.step', { current: guide.index + 1, total: guide.steps.length }) }}
          </span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="x-small"
            :aria-label="t('chat.guide.close')"
            @click="guide.close()"
          />
        </div>
        <p
          class="page-guide__text"
          aria-live="polite"
        >
          {{ guide.step.text }}
        </p>
        <p
          v-if="note"
          class="page-guide__note"
        >
          {{ note }}
        </p>
        <div class="page-guide__actions">
          <m-btn
            v-if="guide.index > 0"
            variant="text"
            size="small"
            @click="guide.back()"
          >
            {{ t('chat.guide.back') }}
          </m-btn>
          <m-btn
            size="small"
            color="primary"
            @click="guide.next()"
          >
            {{ guide.isLast ? t('chat.guide.done') : t('chat.guide.next') }}
          </m-btn>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
// Above the dialogs of Vuetify, so a guide step can point into a dialog.
$guide-z-index: 2600;

.page-guide {
  // The ring and the dim area let each click through to the page. Only the bubble takes clicks.
  pointer-events: none;

  &__ring {
    position: fixed;
    z-index: $guide-z-index;
    border: 2px solid rgb(var(--v-theme-primary));
    border-radius: 8px;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.32);
    transition:
      top 0.15s ease,
      left 0.15s ease,
      width 0.15s ease,
      height 0.15s ease;
  }

  &__bubble {
    position: fixed;
    z-index: $guide-z-index + 1;
    pointer-events: auto;
    padding: 12px 12px 8px 16px;
    border-radius: 12px;
    background: rgb(var(--v-theme-surface));
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__count {
    flex: 1;
    font-size: 12px;
    color: var(--color-ds-secondary);
  }

  &__text {
    margin: 6px 0 0;
    font-size: 14px;
    line-height: 20px;
  }

  &__note {
    margin: 6px 0 0;
    font-size: 12px;
    line-height: 18px;
    color: var(--color-ds-secondary);
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 4px;
    margin-top: 8px;
  }
}
</style>
